import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { api } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import {
  VpsPageHeader,
  fmt,
  labelFollowRunner,
  normalizeFollowRunner,
  useVpsAgents
} from './vpsShared';

const EMPTY_DRAFT = {
  name: '',
  followRunner: 'v3',
  inputDevice: 'trackpad',
  handedness: 'right',
  maxAccounts: 500
};

const MAX_TEST_TARGETS = 15;

function parseTargetUsernames(text) {
  return [...new Set(
    String(text || '')
      .split(/[\s,;]+/)
      .map((s) => s.replace(/^@/, '').trim().toLowerCase())
      .filter(Boolean)
  )].slice(0, MAX_TEST_TARGETS);
}

function isLiveAgentStatus(status) {
  return ['running', 'pausing', 'warmup', 'paused', 'preparing'].includes(String(status || ''));
}

function TestProfileModal({ profile, agents, selectedId, onSelectAgent, onClose }) {
  const [accountId, setAccountId] = useState('');
  const [targetsText, setTargetsText] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [busy, setBusy] = useState(false);

  const agent = agents.find((a) => String(a._id) === String(selectedId)) || null;

  useEffect(() => {
    if (!selectedId) {
      setAccounts([]);
      setAccountId('');
      return undefined;
    }
    let cancelled = false;
    setLoadingAccounts(true);
    setAccountId('');
    api.get(`/admin/va/ai-agents/${selectedId}/automation-dashboard`, {
      params: { poolStats: 0 }
    }).then((res) => {
      if (cancelled) return;
      const rows = (res.data?.accounts || []).filter(
        (row) => String(row?.profileStatus || 'active').toLowerCase() === 'active'
      );
      setAccounts(rows);
    }).catch((err) => {
      if (cancelled) return;
      setAccounts([]);
      toast.error(err.response?.data?.error || 'Failed to load logged-in accounts');
    }).finally(() => {
      if (!cancelled) setLoadingAccounts(false);
    });
    return () => { cancelled = true; };
  }, [selectedId]);

  const sortedAccounts = useMemo(() => {
    const pid = String(profile?._id || '');
    return [...accounts].sort((a, b) => {
      const aMatch = String(a.userProfileId || '') === pid ? 0 : 1;
      const bMatch = String(b.userProfileId || '') === pid ? 0 : 1;
      if (aMatch !== bMatch) return aMatch - bMatch;
      return String(a.username || '').localeCompare(String(b.username || ''));
    });
  }, [accounts, profile?._id]);

  const targets = parseTargetUsernames(targetsText);
  const live = isLiveAgentStatus(agent?.status);
  const canLaunch = Boolean(selectedId && accountId && targets.length && !live && agent?.online);

  const launch = async () => {
    if (!canLaunch) {
      if (!selectedId) toast.error('Pick a VPS');
      else if (!agent?.online) toast.error('That VPS desktop agent is offline');
      else if (live) toast.error('Stop the running automation on that VPS first');
      else if (!accountId) toast.error('Pick a logged-in username');
      else if (!targets.length) toast.error('Paste at least one username to follow');
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(
        `/admin/va/ai-agents/${selectedId}/follow-user-profiles/${profile._id}/test`,
        { accountId, targets }
      );
      if (!res.data?.success) throw new Error(res.data?.error || 'Test failed');
      toast.success(
        `Test launched on ${agent?.name || 'VPS'}: @${res.data.username} → ${res.data.targets} handle(s)`
      );
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to launch test');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={() => !busy && onClose()} />
      <div className="relative w-full max-w-md rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Test {profile.name}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {labelFollowRunner(profile.followRunner)}
              {' · '}
              {profile.inputDevice}
              {' · '}
              {profile.handedness}
              {" — one logged-in account, pasted handles, this profile's feel."}
            </p>
          </div>
          <button
            type="button"
            onClick={() => !busy && onClose()}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <label className="text-xs space-y-1 block">
          <span className="text-gray-500 uppercase tracking-wide">VPS</span>
          <select
            className="form-select text-sm w-full"
            value={selectedId || ''}
            disabled={busy}
            onChange={(e) => onSelectAgent(e.target.value)}
          >
            {!agents.length && <option value="">No agents</option>}
            {agents.map((a) => (
              <option key={a._id} value={a._id}>
                {a.name}
                {a.online ? '' : ' · offline'}
                {a.status && a.status !== 'idle' ? ` · ${a.status}` : ''}
              </option>
            ))}
          </select>
        </label>

        {live && (
          <p className="text-xs text-amber-700 dark:text-amber-400">
            This VPS is busy ({agent.status}). Stop it on Automations before testing.
          </p>
        )}
        {agent && !agent.online && (
          <p className="text-xs text-amber-700 dark:text-amber-400">
            Desktop agent is offline on this VPS.
          </p>
        )}

        <label className="text-xs space-y-1 block">
          <span className="text-gray-500 uppercase tracking-wide">Logged-in username</span>
          <select
            className="form-select text-sm w-full"
            value={accountId}
            disabled={busy || loadingAccounts || !selectedId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            <option value="">
              {loadingAccounts ? 'Loading…' : (sortedAccounts.length ? 'Select account' : 'No logged-in accounts')}
            </option>
            {sortedAccounts.map((row) => {
              const onProfile = String(row.userProfileId || '') === String(profile._id);
              return (
                <option key={row.accountId} value={row.accountId}>
                  @{row.username}{onProfile ? ' · this profile' : ''}
                </option>
              );
            })}
          </select>
        </label>

        <label className="text-xs space-y-1 block">
          <span className="text-gray-500 uppercase tracking-wide">
            Usernames to follow (up to {MAX_TEST_TARGETS})
          </span>
          <textarea
            className="form-input text-sm w-full min-h-[5.5rem]"
            placeholder={'one per line, e.g.\nelonmusk\njack'}
            value={targetsText}
            disabled={busy}
            onChange={(e) => setTargetsText(e.target.value)}
          />
          <span className="text-gray-400">
            {targets.length ? `${targets.length} handle${targets.length === 1 ? '' : 's'}` : 'Paste @usernames'}
          </span>
        </label>

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-secondary !py-1.5 !px-3 text-sm" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary !py-1.5 !px-3 text-sm"
            disabled={busy || !canLaunch}
            onClick={launch}
          >
            {busy ? 'Starting…' : 'Test'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function VpsUserProfiles() {
  const { user } = useAuth();
  const { agents, selectedId, selectAgent } = useVpsAgents();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [editingId, setEditingId] = useState('');
  const [busy, setBusy] = useState(false);
  const [testProfile, setTestProfile] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/va/ai-agents/follow-user-profiles');
      if (res.data?.success) setProfiles(res.data.profiles || []);
      else toast.error(res.data?.error || 'Failed to load user profiles');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load user profiles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (user?.role !== 'admin') {
    return <div className="text-sm text-gray-500">Admin access required.</div>;
  }

  const save = async () => {
    const name = String(draft.name || '').trim();
    if (!name) {
      toast.error('Name is required');
      return;
    }
    setBusy(true);
    try {
      if (editingId) {
        const res = await api.patch(`/admin/va/ai-agents/follow-user-profiles/${editingId}`, draft);
        if (!res.data?.success) throw new Error(res.data?.error || 'Save failed');
        toast.success('User profile updated');
      } else {
        const res = await api.post('/admin/va/ai-agents/follow-user-profiles', draft);
        if (!res.data?.success) throw new Error(res.data?.error || 'Create failed');
        toast.success('User profile created');
      }
      setDraft(EMPTY_DRAFT);
      setEditingId('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (row) => {
    setEditingId(String(row._id));
    setDraft({
      name: row.name || '',
      followRunner: normalizeFollowRunner(row.followRunner, 'v3'),
      inputDevice: row.inputDevice === 'mouse' ? 'mouse' : 'trackpad',
      handedness: row.handedness === 'left' ? 'left' : 'right',
      maxAccounts: Number(row.maxAccounts) || 500
    });
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete “${row.name}”? Accounts on it will need to be unassigned first unless you force.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/va/ai-agents/follow-user-profiles/${row._id}`);
      toast.success('Deleted');
      if (editingId === String(row._id)) {
        setEditingId('');
        setDraft(EMPTY_DRAFT);
      }
      await load();
    } catch (err) {
      const msg = err.response?.data?.error || 'Delete failed';
      if (String(msg).includes('still has')) {
        if (window.confirm(`${msg}\nUnassign everyone and delete?`)) {
          try {
            await api.delete(`/admin/va/ai-agents/follow-user-profiles/${row._id}`, { params: { force: 1 } });
            toast.success('Deleted');
            await load();
          } catch (err2) {
            toast.error(err2.response?.data?.error || 'Delete failed');
          }
        }
      } else toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <VpsPageHeader
        title="User profiles"
        subtitle="Sticky X.com browser feel (device, hand, URL follow, Humanized 1, or Humanized 2). Cap is per VPS — each agent fills 0 → max independently. Fleet is the total across all agents."
      />

      <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-3">
        <div className="text-xs uppercase tracking-wide text-gray-500">
          {editingId ? 'Edit profile' : 'New profile'}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
          <label className="text-xs space-y-1 xl:col-span-2">
            <span className="text-gray-500 uppercase tracking-wide">Name</span>
            <input
              className="form-input text-sm w-full"
              value={draft.name}
              maxLength={80}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder="V2.1"
            />
          </label>
          <label className="text-xs space-y-1">
            <span className="text-gray-500 uppercase tracking-wide">Runner</span>
            <select
              className="form-select text-sm w-full"
              value={draft.followRunner}
              onChange={(e) => setDraft((d) => ({ ...d, followRunner: e.target.value }))}
            >
              <option value="v3">Humanized 1</option>
              <option value="h2">Humanized 2</option>
              <option value="classic">URL follow</option>
            </select>
          </label>
          <label className="text-xs space-y-1">
            <span className="text-gray-500 uppercase tracking-wide">Device</span>
            <select
              className="form-select text-sm w-full"
              value={draft.inputDevice}
              onChange={(e) => setDraft((d) => ({ ...d, inputDevice: e.target.value }))}
            >
              <option value="trackpad">Trackpad</option>
              <option value="mouse">Mouse</option>
            </select>
          </label>
          <label className="text-xs space-y-1">
            <span className="text-gray-500 uppercase tracking-wide">Hand</span>
            <select
              className="form-select text-sm w-full"
              value={draft.handedness}
              onChange={(e) => setDraft((d) => ({ ...d, handedness: e.target.value }))}
            >
              <option value="right">Right</option>
              <option value="left">Left</option>
            </select>
          </label>
          <label className="text-xs space-y-1">
            <span className="text-gray-500 uppercase tracking-wide">Max accounts</span>
            <input
              type="number"
              min={1}
              max={2000}
              className="form-input text-sm w-full"
              value={draft.maxAccounts}
              onChange={(e) => setDraft((d) => ({ ...d, maxAccounts: Number(e.target.value) || 1 }))}
            />
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-primary !py-2 !px-4 text-sm" disabled={busy} onClick={save}>
            {editingId ? 'Save' : 'Create'}
          </button>
          {editingId && (
            <button
              type="button"
              className="btn-secondary !py-2 !px-4 text-sm"
              disabled={busy}
              onClick={() => { setEditingId(''); setDraft(EMPTY_DRAFT); }}
            >
              Cancel
            </button>
          )}
        </div>
      </section>

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Name</th>
              <th className="px-3 py-2 text-left font-medium">Runner</th>
              <th className="px-3 py-2 text-left font-medium">Device</th>
              <th className="px-3 py-2 text-left font-medium">Hand</th>
              <th className="px-3 py-2 text-right font-medium">Fleet</th>
              <th className="px-3 py-2 text-right font-medium">Per VPS cap</th>
              <th className="px-3 py-2 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {loading && !profiles.length && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-gray-500">Loading…</td>
              </tr>
            )}
            {profiles.map((row) => (
              <tr key={row._id} className="hover:bg-gray-50/80 dark:hover:bg-gray-800/40">
                <td className="px-3 py-2.5 font-medium text-gray-900 dark:text-white">{row.name}</td>
                <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{labelFollowRunner(row.followRunner)}</td>
                <td className="px-3 py-2.5 capitalize">{row.inputDevice}</td>
                <td className="px-3 py-2.5 capitalize">{row.handedness}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{fmt(row.accountCount)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{fmt(row.maxAccounts)}</td>
                <td className="px-3 py-2.5 text-right">
                  <button type="button" className="text-indigo-600 dark:text-indigo-400 hover:underline text-xs mr-3" onClick={() => setTestProfile(row)}>
                    Test
                  </button>
                  <button type="button" className="text-indigo-600 dark:text-indigo-400 hover:underline text-xs mr-3" onClick={() => startEdit(row)}>
                    Edit
                  </button>
                  <button type="button" className="text-red-600 dark:text-red-400 hover:underline text-xs" disabled={busy} onClick={() => remove(row)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {!loading && !profiles.length && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-gray-500">No user profiles yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {testProfile && (
        <TestProfileModal
          profile={testProfile}
          agents={agents}
          selectedId={selectedId}
          onSelectAgent={selectAgent}
          onClose={() => setTestProfile(null)}
        />
      )}
    </div>
  );
}
